import React, { FC, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Heading1, Breadcrumbs } from '@blueprint/ui';
import { withOptimizely, WithOptimizelyProps } from '@optimizely/react-sdk';

import {
    SearchResultsHeaderWrapper,
    SearchResultsTotalText,
    Footer,
    FooterCell,
    Divider,
    BreadcrumbsShowDesktopWrapper
} from './SearchResultsHeader.styles';
import { totalResultsTextSelector } from '../../../reducers/searchResults';
import { MapIcon } from './MapIcon';
import { FiltersModal } from '../../FiltersModal';
import { Breadcrumb } from '../../SearchContext';
import { EditSavedSearchBanner } from '../../banners/EditSavedSearchBanner';
import { SectionThatAppearsWhenNoSideBar } from '../../Layout/Layout.styles';
import { isShowEditSavedSearchBanner } from '../../helpers';
import { LayoutVersion } from '../../../constants';

const Heading: FC<{ pageTitle: string } & WithOptimizelyProps> = ({
    pageTitle,
    optimizely
}) => {
    const shouldAddColon =
        'variation_2' === optimizely?.activate('example_a_b_test');
    return (
        <Heading1 data-testid={`results-title`} isRegular>
            {pageTitle}
            {shouldAddColon ? ':' : ''}
        </Heading1>
    );
};

const HeadingWithOptimizely = withOptimizely(Heading);

export type SearchResultsHeaderProps = {
    breadcrumbs: Breadcrumb[];
    pageTitle: string;
    mapIconLink: string;
    isEditSavedSearchBannerVisible: boolean;
    userAlertId: string | null;
    previousSavedSearchUri: string | null;
    savedSearchAndAlertsUri: string | undefined;
    isLoggedIn: boolean | null;
    sortOrderDropdown: React.ReactElement;
};

export const SearchResultsHeader: FC<SearchResultsHeaderProps> = ({
    breadcrumbs,
    mapIconLink,
    pageTitle,
    isEditSavedSearchBannerVisible,
    userAlertId,
    previousSavedSearchUri,
    savedSearchAndAlertsUri,
    isLoggedIn,
    sortOrderDropdown
}) => {
    const totalResultsText = useSelector(totalResultsTextSelector);

    const [showEditSavedSearchBanner, setShowEditSavedSearchBanner] = useState(
        false
    );

    useEffect(() => {
        if (!isEditSavedSearchBannerVisible) {
            setShowEditSavedSearchBanner(false);
        } else if (isLoggedIn) {
            const showEditSavedSearch = isShowEditSavedSearchBanner(
                userAlertId,
                previousSavedSearchUri,
                savedSearchAndAlertsUri
            );

            setShowEditSavedSearchBanner(!!showEditSavedSearch);
        }
    }, [
        isLoggedIn,
        isEditSavedSearchBannerVisible,
        userAlertId,
        previousSavedSearchUri,
        savedSearchAndAlertsUri
    ]);

    return (
        <SearchResultsHeaderWrapper>
            <BreadcrumbsShowDesktopWrapper>
                <Breadcrumbs
                    data-testid="desktop-breadcrumbs"
                    data={breadcrumbs}
                />
            </BreadcrumbsShowDesktopWrapper>

            {showEditSavedSearchBanner ? (
                <SectionThatAppearsWhenNoSideBar data-testid="search-results-header_edit-saved-search-cta">
                    <EditSavedSearchBanner />
                </SectionThatAppearsWhenNoSideBar>
            ) : null}

            <HeadingWithOptimizely pageTitle={pageTitle} />

            <Footer>
                <FooterCell>
                    <SearchResultsTotalText data-testid="total-results" isBold>
                        {totalResultsText}
                    </SearchResultsTotalText>
                    <Divider />
                    <MapIcon href={mapIconLink} />
                </FooterCell>
                <FooterCell>
                    <FiltersModal
                        version={LayoutVersion.Desktop}
                        testId="search-results-header_filters-button"
                    />
                    {sortOrderDropdown}
                </FooterCell>
            </Footer>
        </SearchResultsHeaderWrapper>
    );
};
